import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import {
  fetchRolloutAudit,
  fetchRolloutStatus,
  updateRolloutSettings,
  upsertRolloutOverride,
  deleteRolloutOverride,
} from '@/lib/orchestrationRolloutApi';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import type {
  AgenticRolloutAuditLogRow,
  AgenticRolloutOverrideRow,
  AgenticRolloutSettingsRow,
} from '@shared/schema';
import { Loader2, ShieldCheck, Activity, ListChecks } from 'lucide-react';

type SettingsDraft = {
  rolloutStage: AgenticRolloutSettingsRow['rolloutStage'];
  rolloutPercentage: number;
  stickinessMinutes: number;
  killSwitch: boolean;
  defaultCohort: string;
  decisionSalt: string;
};

const STAGE_OPTIONS: AgenticRolloutSettingsRow['rolloutStage'][] = [
  'internal',
  'staff',
  'alpha',
  'beta',
  'ga',
];

const OVERRIDE_MODES = [
  { value: 'force_v1', label: 'Force v1' },
  { value: 'force_v2', label: 'Force v2' },
] as const;

const formatDateTime = (value?: string | Date | null) => {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const summarizeAudit = (entry: AgenticRolloutAuditLogRow) => {
  if (entry.action === 'UPDATE_SETTINGS') {
    const prev = (entry.previousState as Record<string, any>) ?? {};
    const curr = (entry.currentState as Record<string, any>) ?? {};
    return `Stage ${prev.rolloutStage ?? '—'} → ${curr.rolloutStage ?? '—'}, ` +
      `Percent ${prev.rolloutPercentage ?? '—'} → ${curr.rolloutPercentage ?? '—'}`;
  }
  if (entry.action === 'UPSERT_OVERRIDE') {
    const curr = (entry.currentState as Record<string, any>) ?? {};
    return `Override user ${curr.userId ?? '—'} mode ${curr.mode ?? '—'}`;
  }
  if (entry.action === 'DELETE_OVERRIDE') {
    const prev = (entry.previousState as Record<string, any>) ?? {};
    return `Removed override for user ${prev.userId ?? '—'}`;
  }
  return entry.action;
};

const OrchestrationRolloutPage = () => {
  return (
    <AdminLayout
      title="Agentic Orchestration Rollout"
      description="Configure ENABLE_AGENTIC_ORCHESTRATION rollout stages, overrides, and audit history."
    >
      <RolloutContent />
    </AdminLayout>
  );
};

const RolloutContent = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const statusQuery = useQuery({
    queryKey: ['agentic-rollout-status'],
    queryFn: fetchRolloutStatus,
  });
  const auditQuery = useQuery({
    queryKey: ['agentic-rollout-audit'],
    queryFn: () => fetchRolloutAudit(40),
  });

  const [settingsDraft, setSettingsDraft] = useState<SettingsDraft>({
    rolloutStage: 'internal',
    rolloutPercentage: 0,
    stickinessMinutes: 15,
    killSwitch: false,
    defaultCohort: 'control',
    decisionSalt: 'agentic-orchestration',
  });

  useEffect(() => {
    if (statusQuery.data?.settings) {
      const next = statusQuery.data.settings;
      setSettingsDraft({
        rolloutStage: next.rolloutStage,
        rolloutPercentage: next.rolloutPercentage,
        stickinessMinutes: Math.max(
          1,
          Math.round((next.stickinessTtlMs ?? 900000) / 60000),
        ),
        killSwitch: next.killSwitch,
        defaultCohort: next.defaultCohort ?? 'control',
        decisionSalt: next.decisionSalt ?? 'agentic-orchestration',
      });
    }
  }, [statusQuery.data]);

  const baseline = statusQuery.data?.settings;
  const isDirty = useMemo(() => {
    if (!baseline) return false;
    const baselineMinutes = Math.max(
      1,
      Math.round((baseline.stickinessTtlMs ?? 900000) / 60000),
    );
    return (
      baseline.rolloutStage !== settingsDraft.rolloutStage ||
      baseline.rolloutPercentage !== settingsDraft.rolloutPercentage ||
      baselineMinutes !== settingsDraft.stickinessMinutes ||
      baseline.killSwitch !== settingsDraft.killSwitch ||
      (baseline.defaultCohort ?? 'control') !== settingsDraft.defaultCohort ||
      (baseline.decisionSalt ?? 'agentic-orchestration') !==
        settingsDraft.decisionSalt
    );
  }, [baseline, settingsDraft]);

  const settingsMutation = useMutation({
    mutationFn: updateRolloutSettings,
    onSuccess: () => {
      toast({ title: 'Rollout settings updated' });
      void queryClient.invalidateQueries({
        queryKey: ['agentic-rollout-status'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['agentic-rollout-audit'],
      });
    },
    onError: (error) => {
      toast({
        title: 'Unable to update settings',
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  const overrideMutation = useMutation({
    mutationFn: upsertRolloutOverride,
    onSuccess: () => {
      toast({ title: 'Override saved' });
      void queryClient.invalidateQueries({
        queryKey: ['agentic-rollout-status'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['agentic-rollout-audit'],
      });
    },
    onError: (error) => {
      toast({
        title: 'Unable to save override',
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const deleteOverrideMutation = useMutation({
    mutationFn: deleteRolloutOverride,
    onMutate: async (userId) => {
      setPendingDelete(userId);
      await queryClient.cancelQueries({
        queryKey: ['agentic-rollout-status'],
      });
    },
    onSuccess: () => {
      toast({ title: 'Override removed' });
      void queryClient.invalidateQueries({
        queryKey: ['agentic-rollout-status'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['agentic-rollout-audit'],
      });
    },
    onError: (error) => {
      toast({
        title: 'Unable to delete override',
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    },
    onSettled: () => setPendingDelete(null),
  });

  const [overrideForm, setOverrideForm] = useState<{
    userId: string;
    mode: 'force_v1' | 'force_v2';
    cohort: string;
    reason: string;
    expiresAt: string;
  }>({
    userId: '',
    mode: 'force_v1',
    cohort: 'variant',
    reason: '',
    expiresAt: '',
  });

  const handleOverrideSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsedId = Number.parseInt(overrideForm.userId, 10);
    if (!Number.isFinite(parsedId) || parsedId <= 0) {
      toast({
        title: 'Invalid user ID',
        description: 'Please enter a positive numeric user ID.',
        variant: 'destructive',
      });
      return;
    }
    overrideMutation.mutate({
      userId: parsedId,
      mode: overrideForm.mode,
      cohort: overrideForm.cohort.trim() || undefined,
      reason: overrideForm.reason.trim() || undefined,
      expiresAt: overrideForm.expiresAt
        ? new Date(overrideForm.expiresAt).toISOString()
        : undefined,
    });
    setOverrideForm({
      userId: '',
      mode: 'force_v1',
      cohort: 'variant',
      reason: '',
      expiresAt: '',
    });
  };

  const handleSettingsSave = () => {
    settingsMutation.mutate({
      rolloutStage: settingsDraft.rolloutStage,
      rolloutPercentage: Math.min(
        100,
        Math.max(0, settingsDraft.rolloutPercentage),
      ),
      stickinessTtlMs: Math.max(1, settingsDraft.stickinessMinutes) * 60000,
      killSwitch: settingsDraft.killSwitch,
      defaultCohort: settingsDraft.defaultCohort.trim() || undefined,
      decisionSalt: settingsDraft.decisionSalt.trim() || undefined,
    });
  };

  const overrides = statusQuery.data?.overrides ?? [];
  const cohorts = statusQuery.data?.settings?.abCohorts ?? [];

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-orange-600" />
                Rollout Settings
              </CardTitle>
              <CardDescription>
                Control ramp stages, stickiness, and kill switch behavior.
              </CardDescription>
            </div>
            <Badge variant={statusQuery.data?.flagEnabled ? 'default' : 'secondary'}>
              {statusQuery.data?.flagEnabled ? 'Flag Enabled' : 'Flag Disabled'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {statusQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading rollout configuration…
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600">
                    Rollout Stage
                  </label>
                  <Select
                    value={settingsDraft.rolloutStage}
                    onValueChange={(value) =>
                      setSettingsDraft((prev) => ({
                        ...prev,
                        rolloutStage: value as SettingsDraft['rolloutStage'],
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGE_OPTIONS.map((stage) => (
                        <SelectItem key={stage} value={stage}>
                          {stage.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600">
                    Rollout Percentage
                  </label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={settingsDraft.rolloutPercentage}
                    onChange={(event) =>
                      setSettingsDraft((prev) => ({
                        ...prev,
                        rolloutPercentage: Number.parseInt(
                          event.target.value,
                          10,
                        ),
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600">
                    Stickiness TTL (minutes)
                  </label>
                  <Input
                    type="number"
                    min={1}
                    value={settingsDraft.stickinessMinutes}
                    onChange={(event) =>
                      setSettingsDraft((prev) => ({
                        ...prev,
                        stickinessMinutes: Number.parseInt(
                          event.target.value,
                          10,
                        ),
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600">
                    Default Cohort
                  </label>
                  <Input
                    value={settingsDraft.defaultCohort}
                    onChange={(event) =>
                      setSettingsDraft((prev) => ({
                        ...prev,
                        defaultCohort: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600">
                    Decision Salt
                  </label>
                  <Input
                    value={settingsDraft.decisionSalt}
                    onChange={(event) =>
                      setSettingsDraft((prev) => ({
                        ...prev,
                        decisionSalt: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600">
                    Kill Switch
                  </label>
                  <div className="flex items-center gap-3 rounded-md border border-slate-200 p-3">
                    <Switch
                      checked={settingsDraft.killSwitch}
                      onCheckedChange={(checked) =>
                        setSettingsDraft((prev) => ({
                          ...prev,
                          killSwitch: checked,
                        }))
                      }
                    />
                    <span className="text-sm text-slate-600">
                      {settingsDraft.killSwitch
                        ? 'Kill switch active (all cohorts forced to v1)'
                        : 'Kill switch disabled'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-600 mb-2">
                  Test Cohorts
                </p>
                {cohorts.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    No cohorts configured.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {cohorts.map((cohort) => (
                      <Badge key={cohort.name} variant="outline">
                        {cohort.name} · {cohort.percentage}% ·{' '}
                        {cohort.allowV2 ? 'V2 opt-in' : 'Holdback'}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={handleSettingsSave}
                  disabled={
                    !isDirty || settingsMutation.isPending || statusQuery.isLoading
                  }
                >
                  {settingsMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving
                    </>
                  ) : (
                    'Save settings'
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-orange-600" />
            Per-user Overrides
          </CardTitle>
          <CardDescription>
            Force v1 or v2 for specific users with optional expiration.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form
            className="grid gap-4 md:grid-cols-4"
            onSubmit={handleOverrideSubmit}
          >
            <Input
              required
              type="number"
              min={1}
              placeholder="User ID"
              value={overrideForm.userId}
              onChange={(event) =>
                setOverrideForm((prev) => ({
                  ...prev,
                  userId: event.target.value,
                }))
              }
            />
            <Select
              value={overrideForm.mode}
              onValueChange={(value) =>
                setOverrideForm((prev) => ({
                  ...prev,
                  mode: value as 'force_v1' | 'force_v2',
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Override mode" />
              </SelectTrigger>
              <SelectContent>
                {OVERRIDE_MODES.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Cohort (optional)"
              value={overrideForm.cohort}
              onChange={(event) =>
                setOverrideForm((prev) => ({
                  ...prev,
                  cohort: event.target.value,
                }))
              }
            />
            <Input
              type="datetime-local"
              value={overrideForm.expiresAt}
              onChange={(event) =>
                setOverrideForm((prev) => ({
                  ...prev,
                  expiresAt: event.target.value,
                }))
              }
            />
            <div className="md:col-span-3">
              <Textarea
                placeholder="Reason (optional)"
                value={overrideForm.reason}
                onChange={(event) =>
                  setOverrideForm((prev) => ({
                    ...prev,
                    reason: event.target.value,
                  }))
                }
              />
            </div>
            <div className="md:col-span-1 flex items-end">
              <Button
                type="submit"
                className="w-full"
                disabled={overrideMutation.isPending}
              >
                {overrideMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving
                  </>
                ) : (
                  'Add override'
                )}
              </Button>
            </div>
          </form>

          <div className="rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User ID</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Cohort</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {overrides.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-slate-500">
                      No overrides configured.
                    </TableCell>
                  </TableRow>
                ) : (
                  overrides.map((override: AgenticRolloutOverrideRow) => (
                    <TableRow key={override.userId}>
                      <TableCell>{override.userId}</TableCell>
                      <TableCell className="capitalize">
                        {override.mode?.replace('_', ' ')}
                      </TableCell>
                      <TableCell>{override.cohort ?? '—'}</TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {override.reason ?? '—'}
                      </TableCell>
                      <TableCell>
                        {formatDateTime(override.expiresAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={pendingDelete === override.userId}
                          onClick={() =>
                            deleteOverrideMutation.mutate(override.userId)
                          }
                        >
                          {pendingDelete === override.userId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Remove'
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-orange-600" />
            Audit Log
          </CardTitle>
          <CardDescription>
            Every settings or override change is captured with actor metadata.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {auditQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading audit log…
            </div>
          ) : (
            <div className="rounded-md border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>When</TableHead>
                    <TableHead>Summary</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(auditQuery.data ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-slate-500">
                        No audit entries yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (auditQuery.data ?? []).map(
                      (entry: AgenticRolloutAuditLogRow) => (
                        <TableRow key={entry.id}>
                          <TableCell>{entry.action}</TableCell>
                          <TableCell>{entry.actorId ?? 'system'}</TableCell>
                          <TableCell>{formatDateTime(entry.createdAt)}</TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {summarizeAudit(entry)}
                          </TableCell>
                        </TableRow>
                      ),
                    )
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrchestrationRolloutPage;
